import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';

// Import slick-carousel CSS
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { Layout } from './Style';
import BackIcon from '../../../../components/ui/icons/BackIcon';
import Seo from '../../../../components/seo/Seo';

// Styled components
const Page = styled.div`
  max-width: var(--page-width);
  margin: 0 auto;
  padding: 0 var(--gutter);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2.5rem;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1.15rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 1.4rem;
  text-decoration: none;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    path {
      fill: currentColor;
    }
  }

  &:hover {
    border-color: var(--ink);
    transform: translateY(-1px);
  }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 2.8rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const LabelPill = styled.span`
  width: fit-content;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  text-transform: uppercase;
  letter-spacing: 0.14rem;
  font-size: 1.4rem;
`;

const Title = styled.h1`
  font-size: clamp(3.8rem, 5.2vw, 7.2rem);
  margin: 0;
  line-height: 1.08;
  color: var(--ink);
  font-weight: 500;
`;

const Subtitle = styled.h2`
  font-size: clamp(1.4rem, 2.2vw, 1.9rem);
  margin: 0;
  color: var(--ink);
  font-weight: 500;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem 1.4rem;
  font-size: 1.4rem;
  text-transform: uppercase;
  letter-spacing: 0.12rem;
  color: var(--muted);
`;

const Description = styled.p`
  font-size: 1.6rem;
  line-height: 1.7;
  color: var(--ink);
  margin: 0;
`;

const HeroMedia = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  aspect-ratio: 16 / 10;
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  border-radius: 6px;
  box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
  filter: saturate(0.98) contrast(1.02);
`;

const Section = styled.section`
  margin: 6rem 0;
  padding-top: 2.5rem;
  border-top: 1px solid var(--line);
`;

const SectionTitle = styled.h3`
  font-size: clamp(2.4rem, 3vw, 4rem);
  margin-bottom: 1.2rem;
  color: var(--ink);
`;

const PillGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Pill = styled.span`
  padding: 0.55rem 1rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font-size: 1.4rem;
`;

const HighlightsGrid = styled.div`
  display: grid;
  gap: 0.8rem;
  color: var(--ink);

  div {
    position: relative;
    padding-left: 1.2rem;
    font-size: 1.4rem;
    line-height: 1.5;
  }

  div::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55rem;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--surface);
  }
`;

const CarouselContainer = styled.div`
  margin-top: 1.5rem;

  .slick-arrow {
    width: 44px;
    height: 44px;
    z-index: 2;
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 50%;
  }
  .slick-prev {
    left: 8px;
  }
  .slick-next {
    right: 8px;
  }
  .slick-arrow:hover,
  .slick-arrow:focus {
    background: var(--paper);
  }
  button.slick-arrow:before {
    color: var(--ink);
  }

  .slick-slide {
    outline: none;
  }

  .slick-dots li button:before {
    color: var(--muted);
  }

  .slick-dots li.slick-active button:before {
    color: var(--ink);
  }
`;

const Slide = styled.div`
  position: relative;
`;

const SlideImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 6px;
  object-fit: cover;
`;

const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const ToggleSwitch = styled.div`
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: 999px;
  overflow: hidden;
  background: var(--surface);
`;

const ToggleOption = styled.button<{ $active?: boolean }>`
  font-size: 1.4rem;
  padding: 1.1rem 1.6rem;
  min-height: 44px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'var(--ink)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--paper)' : 'var(--muted)')};
  border: none;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition:
    background 0.2s ease,
    color 0.2s ease;
`;

const SliderMobile = styled(Slider)`
  max-width: 32rem;
  margin: 0 auto;
`;

// Main Component
const PortfolioItem = () => {
  const { t, i18n } = useTranslation('ourWork');
  const { slug } = useParams();
  const [activeSlider, setActiveSlider] = useState('desktop');

  // Retrieve the projects from translation files and convert to an array
  const projectsTranslations = useMemo(
    () => t('projects', { returnObjects: true }),
    [t, i18n.language],
  );

  const projects = useMemo(
    () =>
      Object.keys(projectsTranslations).map((key) => ({
        ...projectsTranslations[key],
      })),
    [projectsTranslations],
  );

  const normalizedSlug = useMemo(() => {
    if (!slug) return '';
    try {
      return decodeURIComponent(slug)
        .replace(/^\/+|\/+$/g, '')
        .toLowerCase();
    } catch {
      return slug.replace(/^\/+|\/+$/g, '').toLowerCase();
    }
  }, [slug]);

  // Find the project item based on the URL slug (with fallbacks).
  const matchedItem = useMemo(() => {
    if (!normalizedSlug) return undefined;
    return projects.find((item) => {
      const itemSlug = (item.slug || '').toLowerCase();
      if (itemSlug === normalizedSlug) return true;
      const internalLink = (item.internalLink || '').toLowerCase();
      if (internalLink.endsWith(`/${normalizedSlug}`)) return true;
      const internalLast = internalLink.split('/').filter(Boolean).pop();
      return internalLast === normalizedSlug;
    });
  }, [projects, normalizedSlug]);

  const canonicalPath =
    (matchedItem?.internalLink as string | undefined) ||
    `/${i18n.language?.startsWith('fr') ? 'fr' : 'en'}/our-work/${slug ?? ''}`;

  const seoTitle = matchedItem
    ? `${matchedItem.title2 || matchedItem.title} | ${matchedItem.label || 'Project'} | SOR SEO`
    : 'Project | SOR SEO';

  const seoDescription =
    matchedItem?.description ||
    'Browse SOR SEO case studies covering custom software, web apps, and SEO programs.';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    setActiveSlider(
      matchedItem?.images?.length || matchedItem?.mainImage
        ? 'desktop'
        : 'mobile',
    );
  }, [matchedItem]);

  if (!matchedItem) {
    return (
      <Layout>
        <Page>
          <Title>{t('notFound', 'Portfolio item not found')}</Title>
          <Section>
            <BackButton to={t('navigateLink')}>{t('back', 'BACK')}</BackButton>
          </Section>
        </Page>
      </Layout>
    );
  }

  // Destructure data from the matched project item
  const {
    title,
    title2,
    description,
    mainImage,
    images,
    highlights,
    mobileImages,
    services,
    location,
    country,
    label,
  } = matchedItem;

  // Settings for the main (desktop) carousel
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    arrows: true,
  };

  // Settings for the mobile images carousel
  const mobileCarouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    arrows: true,
  };

  const hasMobile = mobileImages && mobileImages.length > 0;
  const hasDesktop = (images && images.length > 0) || mainImage;
  const scopeItems =
    services && services.length > 0
      ? services
      : highlights && highlights.length > 0
        ? highlights
        : [];

  return (
    <Layout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
        ogImage={matchedItem?.mainImage}
      />
      <Page>
        {/* Header with back button */}
        <Header>
          <BackButton to={t('navigateLink')} aria-label={t('back', 'BACK')}>
            <BackIcon />
            &nbsp;{t('back', 'BACK')}
          </BackButton>
        </Header>

        <Hero>
          <HeroText>
            {label ? <LabelPill>{label}</LabelPill> : null}
            <Title>{title}</Title>
            {title2 ? <Subtitle>{title2}</Subtitle> : null}
            {(location || country) && (
              <MetaRow>
                <span>{(location || '').toUpperCase()}</span>
                {location && country ? <span>·</span> : null}
                <span>{(country || '').toUpperCase()}</span>
              </MetaRow>
            )}
            {description ? <Description>{description}</Description> : null}
          </HeroText>

          <HeroMedia>
            {mainImage ? (
              <HeroImage src={mainImage} alt={`${title} hero`} />
            ) : null}
          </HeroMedia>
        </Hero>

        <Section>
          <SectionTitle>
            {t('caseStudy.scopeTitle', 'Engagement focus')}
          </SectionTitle>
          <PillGrid>
            {scopeItems.map((serviceItem, index) => (
              <Pill key={`service-${index}`}>{serviceItem}</Pill>
            ))}
          </PillGrid>
        </Section>

        {highlights && highlights.length ? (
          <Section>
            <SectionTitle>
              {t('caseStudy.outcomesTitle', 'Outcomes')}
            </SectionTitle>
            <HighlightsGrid>
              {highlights.map((highlight, index) => (
                <div key={`highlight-${index}`}>{highlight}</div>
              ))}
            </HighlightsGrid>
          </Section>
        ) : null}

        {(hasDesktop || hasMobile) && (
          <Section>
            <SectionTitle>
              {t('caseStudy.galleryTitle', 'Interface gallery')}
            </SectionTitle>
            <ToggleContainer>
              {hasDesktop && hasMobile ? (
                <ToggleSwitch>
                  <ToggleOption
                    type="button"
                    $active={activeSlider === 'desktop'}
                    aria-pressed={activeSlider === 'desktop'}
                    onClick={() => setActiveSlider('desktop')}
                  >
                    Desktop
                  </ToggleOption>
                  <ToggleOption
                    type="button"
                    $active={activeSlider === 'mobile'}
                    aria-pressed={activeSlider === 'mobile'}
                    onClick={() => setActiveSlider('mobile')}
                  >
                    Mobile
                  </ToggleOption>
                </ToggleSwitch>
              ) : (
                <ToggleSwitch>
                  <ToggleOption type="button" $active>
                    {hasDesktop ? 'Desktop' : 'Mobile'}
                  </ToggleOption>
                </ToggleSwitch>
              )}
            </ToggleContainer>

            <CarouselContainer>
              {activeSlider === 'desktop' ? (
                <Slider {...carouselSettings}>
                  {images && images.length > 0 ? (
                    images.map((img, index) => (
                      <Slide key={`desktop-slide-${index}`}>
                        <SlideImage
                          src={img}
                          alt={`Project slide ${index + 1}`}
                        />
                      </Slide>
                    ))
                  ) : (
                    <Slide>
                      <SlideImage src={mainImage} alt="Project main" />
                    </Slide>
                  )}
                </Slider>
              ) : (
                <SliderMobile {...mobileCarouselSettings}>
                  {mobileImages &&
                    mobileImages.map((img, index) => (
                      <Slide key={`mobile-slide-${index}`}>
                        <SlideImage
                          src={img}
                          alt={`Mobile view slide ${index + 1}`}
                        />
                      </Slide>
                    ))}
                </SliderMobile>
              )}
            </CarouselContainer>
          </Section>
        )}
      </Page>
    </Layout>
  );
};

export default PortfolioItem;
